import { useContext, useMemo } from 'react';
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, Spinner } from '@ds';
import { AuthContext } from '@lp/core/auth';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { LightsAndSirens } from '@icons';
import { quoteStatusNames, routeStatusNames } from '@/constants/case/case.constants';
import { CaseStatus, RoutePlanStatus } from '@/api/apis/case';
import { useSetRoutePlanning } from '@/queries/case';
import { RouteBuilderPermissions } from '@/permissions';
import { useUpdateRouteStatus } from '@/hooks/useUpdateRouteStatus';
import { hidden, isAllowedStatus } from '@/utils/helpers';

export const RouteBuilderStatuses = () => {
  const { checkPermission } = useContext(AuthContext);
  const { caseId, routePlanningStatus, quote, status, isLightsSirensRequired } = useCaseDetailsWithReviews();
  const { isSubmitting, updateStatus } = useUpdateRouteStatus();
  const { mutateAsync: setRoutePlanning } = useSetRoutePlanning();
  const quoteStatus = quote?.status ? quoteStatusNames[quote.status] : 'No Quote';

  const canChangeStatus = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.AssignResource);
    const isCorrectStatus = routePlanningStatus === RoutePlanStatus.PendingResourcesBooking;
    const isActiveCaseStatus = isAllowedStatus({
      statusFrom: CaseStatus.PendingLogistics,
      statusTo: CaseStatus.ReturningToBase,
      currentStatus: status,
    });

    return hasPermission && isCorrectStatus && isActiveCaseStatus;
  }, [checkPermission, routePlanningStatus, status]);

  const canRollBackStatus = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.AssignResource);
    const isCorrectStatus =
      routePlanningStatus &&
      [
        RoutePlanStatus.RoutePlanningDone,
        RoutePlanStatus.PendingResourcesBooking,
        RoutePlanStatus.ResourcesBooked,
      ].includes(routePlanningStatus);
    const isActiveCaseStatus = isAllowedStatus({
      statusFrom: CaseStatus.PendingLogistics,
      statusTo: CaseStatus.ReturningToBase,
      currentStatus: status,
    });

    return hasPermission && isCorrectStatus && isActiveCaseStatus;
  }, [checkPermission, routePlanningStatus, status]);

  const baseButtonEndContent = canChangeStatus || canRollBackStatus ? <MdKeyboardArrowDown size={20} /> : null;

  return (
    <div className="flex gap-4 items-center">
      {isLightsSirensRequired && <LightsAndSirens width={24} height={24} />}
      <Dropdown placement="bottom-start" isDisabled={isSubmitting || (!canChangeStatus && !canRollBackStatus)}>
        <DropdownTrigger>
          <Button
            disableRipple
            size="md"
            className="py-0 ps-4 pe-3 h-8 rounded-full bg-white! text-black! opacity-100!"
            endContent={isSubmitting ? <Spinner size="sm" /> : baseButtonEndContent}
          >
            {routeStatusNames[routePlanningStatus!]}
          </Button>
        </DropdownTrigger>
        <DropdownMenu aria-label="Route status actions" itemClasses={{ title: ['text-m-r!'] }} selectionMode="none">
          <DropdownItem
            className={hidden(!canChangeStatus)}
            key="proceedStatus"
            textValue="proceedStatus"
            onClick={() => updateStatus(caseId, RoutePlanStatus.PendingResourcesBooking)}
          >
            Proceed to Resources Assigned
          </DropdownItem>
          <DropdownItem
            className={hidden(!canRollBackStatus)}
            key="rollBackStatus"
            textValue="rollBackStatus"
            onClick={() => setRoutePlanning({ caseId })}
          >
            Rollback to Route Planning
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
      <Button
        disableRipple
        size="md"
        className="py-0 ps-4 pe-3 h-8 rounded-full bg-white! text-black! pointer-events-none"
      >
        {quoteStatus}
      </Button>
    </div>
  );
};
